import { useMemo } from 'react';
import { Section } from '@vnetwork/ui/layout';
import { Skeleton } from '@vnetwork/ui/primitives';
import { Stat, StatGroup } from '@vnetwork/ui/data';
import { useBucketUsageSeries, type UsageSeriesPoint } from '@/api/s3';
import { formatNum } from '@/pages/services/_shared/analytics-format';
import { formatGiB } from '@/pages/services/oss/storage-format';
import { StorageUtilizationChart } from '../overview/StorageUtilizationChart';

/** Latest (most recent day) point of a series, regardless of return order. */
function latestPoint(series: UsageSeriesPoint[]): UsageSeriesPoint | undefined {
  return series.reduce<UsageSeriesPoint | undefined>((acc, p) => {
    if (!acc) return p;
    return new Date(p.ts).getTime() > new Date(acc.ts).getTime() ? p : acc;
  }, undefined);
}

/**
 * Per-bucket storage utilization: latest-day billed/raw/deleted figures plus the
 * daily billed-storage chart. Wasabi-only — the console per-bucket utilization
 * API backs it; other providers answer an empty series, shown as an empty state.
 */
export function BucketUtilizationTab({
  serviceId,
  bucket,
}: {
  serviceId: string;
  bucket: string;
}) {
  const { data: series = [], isLoading } = useBucketUsageSeries(serviceId, bucket);

  // Console returns newest-first; the area chart reads better chronologically.
  const chartSeries = useMemo(
    () => [...series].sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime()),
    [series],
  );
  const latest = useMemo(() => latestPoint(series), [series]);

  return (
    <Section
      title="Storage utilization"
      description={
        latest?.end_ts
          ? `Billed storage for this bucket, as of ${new Date(latest.end_ts).toLocaleDateString()}.`
          : 'Daily billed storage for this bucket.'
      }
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <StatGroup columns={4}>
          <Stat
            label="Billed storage"
            value={
              isLoading ? (
                <Skeleton style={{ height: 28, width: 80 }} />
              ) : (
                formatGiB(latest?.bytes ?? 0)
              )
            }
          />
          <Stat
            label="Deleted storage"
            value={
              isLoading ? (
                <Skeleton style={{ height: 28, width: 80 }} />
              ) : (
                formatGiB(latest?.deleted_bytes ?? 0)
              )
            }
          />
          <Stat
            label="Billable objects"
            value={
              isLoading ? (
                <Skeleton style={{ height: 28, width: 70 }} />
              ) : (
                formatNum(latest?.billable_objects ?? 0)
              )
            }
          />
          <Stat
            label="Storage class"
            value={isLoading ? <Skeleton style={{ height: 28, width: 60 }} /> : latest?.storage_class ?? '—'}
          />
        </StatGroup>

        <StorageUtilizationChart
          series={chartSeries}
          isLoading={isLoading}
          title="Daily billed storage"
          description={`Billed storage for ${bucket} over the reported history.`}
        />
      </div>
    </Section>
  );
}
